import React, { useEffect, useState } from "react";
import { Lock, Unlock, History, AlertTriangle } from "lucide-react";
import { useProject } from "../state/ProjectContext";
import { Modal, Field, Pill } from "./ui-prim";
import { toast } from "../lib/utils";

const EMAIL_KEY = "pmv5_user_email";
const isValidEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(s || "").trim());
const fmt = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch { return iso; }
};

export function getStoredEmail() {
  try { return localStorage.getItem(EMAIL_KEY) || ""; } catch { return ""; }
}
function setStoredEmail(e) {
  try { localStorage.setItem(EMAIL_KEY, e || ""); } catch { /* ignore */ }
}

/**
 * Header button + banner + history modal for project check-in/out.
 * Stores user email in localStorage so subsequent actions pre-fill.
 *
 * Lock semantics: when checked out, only the SAME email can edit. Everyone
 * else sees a read-only banner with the checkout owner's email.
 */
export function CheckInOut() {
  const { doc, mutate, readOnly } = useProject();
  const [askOpen, setAskOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [email, setEmail] = useState(getStoredEmail());

  // Sync the local email field if storage changed elsewhere.
  useEffect(() => { setEmail(getStoredEmail()); }, [askOpen]);

  if (!doc) return null;
  const checkout = doc.checkout || { active: false, email: "", at: "" };
  const log = doc.checkInOutLog || [];
  const isCheckedOut = !!checkout.active;
  const lockedToMe = isCheckedOut && checkout.email.toLowerCase() === getStoredEmail().toLowerCase() && getStoredEmail();

  const submit = () => {
    const e = email.trim();
    if (!isValidEmail(e)) { toast("Enter a valid email"); return; }
    setStoredEmail(e);
    const now = new Date().toISOString();
    const action = isCheckedOut ? "checkin" : "checkout";

    // If trying to check IN but someone else owns it, block.
    if (action === "checkin" && checkout.email.toLowerCase() !== e.toLowerCase()) {
      toast(`Locked to ${checkout.email} — only that user can check in`);
      return;
    }

    mutate(d => ({
      ...d,
      checkout: action === "checkout" ? { active: true, email: e, at: now } : { active: false, email: "", at: "" },
      checkInOutLog: [{ action, email: e, at: now }, ...((d.checkInOutLog) || [])].slice(0, 50),
    }), { bypassLock: true });
    setAskOpen(false);
    toast(action === "checkout" ? `Checked out by ${e}` : `Checked in by ${e}`);
  };

  if (readOnly) return null; // not shown on /share routes

  return (
    <>
      <button
        className={`btn btn-sm ${isCheckedOut ? (lockedToMe ? "btn-primary" : "btn-danger") : "btn-secondary"}`}
        onClick={() => setAskOpen(true)}
        data-testid="btn-checkinout"
        title={isCheckedOut ? `Locked to ${checkout.email}` : "Check out for editing"}
      >
        {isCheckedOut ? <Unlock size={13}/> : <Lock size={13}/>}
        <span className="hidden sm:inline">{isCheckedOut ? "Check In" : "Check Out"}</span>
      </button>
      <button className="btn btn-ghost btn-sm" onClick={() => setHistoryOpen(true)} data-testid="btn-checkout-history" title="Check-in/out history">
        <History size={13}/>
      </button>

      {/* Check-in/out modal */}
      <Modal
        open={askOpen}
        onClose={() => setAskOpen(false)}
        title={isCheckedOut ? "Check in this project" : "Check out this project"}
        footer={
          <>
            <button className="btn btn-ghost" onClick={() => setAskOpen(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={submit} data-testid="btn-confirm-checkinout">
              {isCheckedOut ? "Check In" : "Check Out"}
            </button>
          </>
        }
      >
        {isCheckedOut && (
          <div className="mb-3 p-3 rounded-sm border border-amber-300 bg-amber-50 flex items-start gap-2">
            <AlertTriangle size={14} className="text-amber-700 shrink-0 mt-0.5"/>
            <div className="text-xs text-amber-900">
              Currently checked out by <span className="font-mono font-semibold">{checkout.email}</span> at <span className="font-mono">{fmt(checkout.at)}</span>.
              Only that user can check it back in.
            </div>
          </div>
        )}
        <Field label="Your email">
          <input
            className="fld"
            type="email"
            placeholder="you@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="input-checkout-email"
          />
        </Field>
        <div className="font-mono text-[10px] text-zinc-500">
          {isCheckedOut
            ? "Your email is recorded for the audit log."
            : "Once checked out, only this email can edit the project. Other browsers see a read-only banner."}
        </div>
      </Modal>

      {/* History modal */}
      <Modal
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        title="Check-in / Check-out history"
        size="lg"
      >
        {log.length === 0 ? (
          <div className="text-sm text-zinc-500 italic py-4 text-center">No check-in/out events yet.</div>
        ) : (
          <div className="max-h-[420px] overflow-y-auto" data-testid="checkout-history-list">
            <table className="tbl">
              <thead><tr><th>Action</th><th>Email</th><th>When</th></tr></thead>
              <tbody>
                {log.map((e, i) => (
                  <tr key={i}>
                    <td>
                      <Pill tone={e.action === "checkout" ? "amber" : "green"}>
                        {e.action === "checkout" ? "Check Out" : "Check In"}
                      </Pill>
                    </td>
                    <td className="font-mono text-xs text-zinc-700">{e.email}</td>
                    <td className="font-mono text-xs text-zinc-500">{fmt(e.at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Modal>
    </>
  );
}

/**
 * Banner shown at the top of the workspace whenever the project is checked out.
 * Renders a different tone for "checked out to you" vs "locked to someone else".
 */
export function CheckoutBanner() {
  const { doc, readOnly } = useProject();
  if (!doc) return null;
  const checkout = doc.checkout;
  if (!checkout?.active) return null;
  if (readOnly) return null;

  const me = getStoredEmail();
  const mine = !!me && checkout.email.toLowerCase() === me.toLowerCase();

  return (
    <div
      className={`mb-5 flex items-center gap-3 border rounded-sm px-4 py-3 ${mine ? "border-[var(--border)] bg-[var(--surface)]" : "border-amber-300 bg-amber-50"}`}
      data-testid={mine ? "banner-checkout-mine" : "banner-checkout-locked"}
    >
      {mine ? <Lock size={14} className="text-[var(--navy)] shrink-0"/> : <AlertTriangle size={14} className="text-amber-700 shrink-0"/>}
      <div className="text-xs">
        <span className="font-mono uppercase tracking-[0.14em] text-zinc-500 mr-2">
          {mine ? "Checked out to you" : "Locked"}
        </span>
        <span className={mine ? "text-zinc-700" : "text-amber-900"}>
          {mine
            ? `Edits enabled. Check in when you're done so others can edit.`
            : <>Currently checked out by <span className="font-mono font-semibold">{checkout.email}</span> at <span className="font-mono">{fmt(checkout.at)}</span>. Editing disabled for everyone else.</>}
        </span>
      </div>
    </div>
  );
}

/**
 * Helper: returns true if the doc is locked TO SOMEONE ELSE (not the current
 * stored email). Used by ProjectContext to short-circuit `mutate`.
 */
export function isLockedToOther(doc) {
  const c = doc?.checkout;
  if (!c?.active) return false;
  const me = getStoredEmail();
  if (!me) return true;
  return c.email.toLowerCase() !== me.toLowerCase();
}
